<template>
    <div class="cached-image" :class="{ 'loading': isLoading, 'error': hasError }">
        <img v-if="!hasError" :src="currentSrc" :alt="alt" :class="imageClass" :style="imageStyle" @load="handleLoad"
            @error="handleError" v-bind="$attrs" />

        <!-- 加载状态 -->
        <div v-if="isLoading && showLoading" class="loading-placeholder">
            <div class="loading-spinner"></div>
            <span v-if="loadingText">{{ loadingText }}</span>
        </div>

        <!-- 错误状态 -->
        <div v-if="hasError && showError" class="error-placeholder">
            <div class="error-icon">📷</div>
            <span v-if="errorText">{{ errorText }}</span>
        </div>
    </div>
</template>

<script>
import configCacheService from '../services/configCacheService'

export default {
    name: 'CachedImage',
    inheritAttrs: false,
    props: {
        src: {
            type: String,
            required: true
        },
        alt: {
            type: String,
            default: ''
        },
        cacheKey: {
            type: String,
            default: null
        },
        fallbackSrc: {
            type: String,
            default: null
        },
        imageClass: {
            type: [String, Array, Object],
            default: ''
        },
        imageStyle: {
            type: Object,
            default: () => ({})
        },
        showLoading: {
            type: Boolean,
            default: true
        },
        showError: {
            type: Boolean,
            default: true
        },
        loadingText: {
            type: String,
            default: '加载中...'
        },
        errorText: {
            type: String,
            default: '图片加载失败'
        },
        preload: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentSrc: '',
            isLoading: true,
            hasError: false,
            loadStartTime: 0
        }
    },
    computed: {
        finalCacheKey() {
            return this.cacheKey || configCacheService.generateImageCacheKey(this.src)
        }
    },
    async mounted() {
        await this.loadImage()
    },
    watch: {
        src: {
            handler: 'loadImage',
            immediate: false
        }
    },
    methods: {
        async loadImage() {
            if (!this.src) {
                this.hasError = true
                this.isLoading = false
                return
            }

            this.isLoading = true
            this.hasError = false
            this.loadStartTime = Date.now()

            try {
                // 检查缓存
                const cachedImage = configCacheService.getCachedImage(this.finalCacheKey)
                if (cachedImage) {
                    console.log('✅ 使用缓存图片:', this.src)
                    this.currentSrc = cachedImage.url
                    this.isLoading = false
                    this.$emit('load', cachedImage)
                    return
                }

                // 预加载图片
                if (this.preload) {
                    const imageInfo = await configCacheService.preloadImage(this.src, this.finalCacheKey)
                    this.currentSrc = imageInfo.url
                    this.isLoading = false
                    this.$emit('load', imageInfo)
                } else {
                    // 直接设置src，让浏览器处理
                    this.currentSrc = this.src
                }
            } catch (error) {
                console.error('图片加载失败:', error)
                this.handleError()
            }
        },

        handleLoad(event) {
            const loadTime = Date.now() - this.loadStartTime
            this.isLoading = false

            // 如果没有预加载，现在缓存图片信息
            if (!this.preload) {
                const imageInfo = {
                    url: this.src,
                    width: event.target.naturalWidth,
                    height: event.target.naturalHeight,
                    loadTime: Date.now(),
                    element: event.target
                }
                configCacheService.cacheImage(this.finalCacheKey, imageInfo)
            }

            console.log(`✅ 图片加载完成: ${this.src} (${loadTime}ms)`)
            this.$emit('load', event)
        },

        handleError(event) {
            console.error('❌ 图片加载失败:', this.src)
            this.isLoading = false

            if (this.fallbackSrc && this.currentSrc !== this.fallbackSrc) {
                console.log('🔄 尝试加载备用图片:', this.fallbackSrc)
                this.currentSrc = this.fallbackSrc
                return
            }

            this.hasError = true
            this.$emit('error', event)
        },

        retry() {
            this.hasError = false
            this.loadImage()
        }
    }
}
</script>

<style scoped>
.cached-image {
    position: relative;
    display: inline-block;
}

.loading-placeholder,
.error-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100px;
    background-color: #f5f5f5;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    color: #999;
}

.loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #1890ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 8px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.error-icon {
    font-size: 24px;
    margin-bottom: 8px;
}

.error-placeholder {
    cursor: pointer;
}

.error-placeholder:hover {
    background-color: #fafafa;
}
</style>